/** @format */

import { CheckOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { Avatar, Col, Collapse, Input, Modal, Popconfirm, Row } from "antd";
import moment from "moment";
import React, { FC, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { TCustomPresence } from "../../../../redux-modules/declarations/maps/presences";
import { BaseColor } from "../../../../theme/maps/Colors";
import { hexToRgb } from "../../../../utils/styles";
import { showNotification } from "../../../../utils/showNotification";
import {
  timebankApproveRequest,
  timebankUnapproveRequest,
} from "../../../../utils/timebankActions";
import { UIButton } from "../../../ui/Button";
import { UIText } from "../../../ui/Text";
import { UISelect } from "../../../ui/Select";

const { Panel } = Collapse;
const { Option } = UISelect;

interface ApiErrorShape {
  response?: {
    data?: {
      message?: string;
    };
  };
}

const getErrorMessage = (err: unknown, fallback: string): string => {
  const apiErr = err as ApiErrorShape;
  return apiErr?.response?.data?.message ?? fallback;
};

const GROUP_FALLBACK = "Senza gruppo";
const DEPARTMENT_FALLBACK = "Senza reparto";

const getGroupName = (presence: TCustomPresence): string => {
  const groups = presence.original.user.groups;
  return groups && groups.length > 0 ? groups[0].name : GROUP_FALLBACK;
};

const getDepartmentName = (presence: TCustomPresence): string =>
  presence.original.user.department?.name || DEPARTMENT_FALLBACK;

const getEmployeeName = (presence: TCustomPresence): string =>
  `${presence.original.user.surname} ${presence.original.user.name}`;

// Colori tenui, coerenti col tema del calendario (niente rosso/verde acceso)
const APPROVE_BTN_STYLE = {
  backgroundColor: "#8bc34a",
  borderColor: "#7cb342",
  color: "#ffffff",
};
const REJECT_BTN_STYLE = {
  backgroundColor: "#ef7d7d",
  borderColor: "#e35d5d",
  color: "#ffffff",
};

interface Props {
  visible: boolean;
  onClose: () => void;
  presences: TCustomPresence[];
  canShowBancaore: boolean;
  canShowStraordinario: boolean;
  onRefresh: () => void;
}

export const ManageAnomaliesModal: FC<Props> = ({
  visible,
  onClose,
  presences,
  canShowBancaore,
  canShowStraordinario,
  onRefresh,
}: Props): JSX.Element => {
  const [items, setItems] = useState<TCustomPresence[]>(presences);
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [searchGroups, setSearchGroups] = useState<string[]>([]);
  const [searchEmployee, setSearchEmployee] = useState("");

  useEffect(() => {
    setItems(presences);
  }, [presences]);

  useEffect(() => {
    if (!visible) {
      setSearchGroups([]);
      setSearchEmployee("");
    }
  }, [visible]);

  const label = canShowBancaore ? "Banca Ore" : "Straordinario";

  const removeItem = (id: string) => setItems(prev => prev.filter(p => p.original.id !== id));

  const setRowLoading = (id: string, loading: boolean) =>
    setLoadingIds(prev => (loading ? [...prev, id] : prev.filter(x => x !== id)));

  const approveOne = async (presence: TCustomPresence, silent: boolean = false) => {
    const delta = parseInt(presence.modified?.delta_second_duration ?? "0");
    if (!silent) setRowLoading(presence.original.id, true);
    try {
      await timebankApproveRequest(presence, canShowStraordinario, delta < 0, false, false, false, true);
      removeItem(presence.original.id);
      return true;
    } catch (err) {
      showNotification(
        "error",
        "Errore",
        `${presence.original.user.name} ${presence.original.user.surname}: ${getErrorMessage(
          err,
          "impossibile approvare",
        )}`,
      );
      return false;
    } finally {
      if (!silent) setRowLoading(presence.original.id, false);
    }
  };

  const rejectOne = async (presence: TCustomPresence, silent: boolean = false) => {
    if (!silent) setRowLoading(presence.original.id, true);
    try {
      await timebankUnapproveRequest(presence, canShowStraordinario, true);
      removeItem(presence.original.id);
      return true;
    } catch (err) {
      showNotification(
        "error",
        "Errore",
        `${presence.original.user.name} ${presence.original.user.surname}: ${getErrorMessage(
          err,
          "impossibile rifiutare",
        )}`,
      );
      return false;
    } finally {
      if (!silent) setRowLoading(presence.original.id, false);
    }
  };

  const handleApproveOne = async (presence: TCustomPresence) => {
    if (await approveOne(presence)) {
      showNotification(
        "success",
        "Approvato",
        `${label} approvata per ${presence.original.user.name} ${presence.original.user.surname}`,
      );
      onRefresh();
    }
  };

  const handleRejectOne = async (presence: TCustomPresence) => {
    if (await rejectOne(presence)) {
      showNotification(
        "success",
        "Rifiutato",
        `${label} rifiutata per ${presence.original.user.name} ${presence.original.user.surname}`,
      );
      onRefresh();
    }
  };

  const availableGroups = useMemo(() => {
    const names = new Set(items.map(p => getGroupName(p)));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter(presence => {
      if (searchEmployee) {
        if (!getEmployeeName(presence).toLowerCase().includes(searchEmployee.toLowerCase()))
          return false;
      }
      if (searchGroups.length > 0) {
        if (!searchGroups.includes(getGroupName(presence))) return false;
      }
      return true;
    });
  }, [items, searchEmployee, searchGroups]);

  const groupedItems = useMemo(() => {
    const groupMap = new Map<string, Map<string, TCustomPresence[]>>();

    filteredItems.forEach(presence => {
      const groupName = getGroupName(presence);
      const departmentName = getDepartmentName(presence);

      if (!groupMap.has(groupName)) groupMap.set(groupName, new Map());
      const deptMap = groupMap.get(groupName) as Map<string, TCustomPresence[]>;

      if (!deptMap.has(departmentName)) deptMap.set(departmentName, []);
      (deptMap.get(departmentName) as TCustomPresence[]).push(presence);
    });

    const sortedGroupNames = Array.from(groupMap.keys()).sort((a, b) => a.localeCompare(b));

    return sortedGroupNames.map(groupName => {
      const deptMap = groupMap.get(groupName) as Map<string, TCustomPresence[]>;
      const sortedDepartmentNames = Array.from(deptMap.keys()).sort((a, b) => a.localeCompare(b));

      return {
        groupName,
        departments: sortedDepartmentNames.map(departmentName => ({
          departmentName,
          presences: (deptMap.get(departmentName) as TCustomPresence[]).sort((a, b) =>
            getEmployeeName(a).localeCompare(getEmployeeName(b)),
          ),
        })),
      };
    });
  }, [filteredItems]);

  const handleApproveAll = async () => {
    setBulkLoading(true);
    const results = await Promise.allSettled(filteredItems.map(p => approveOne(p, true)));
    const okCount = results.filter(r => r.status === "fulfilled" && r.value).length;
    const failCount = results.length - okCount;
    setBulkLoading(false);
    if (okCount) showNotification("success", "Approvazione massiva", `${okCount} ${label} approvate.`);
    if (failCount)
      showNotification(
        "error",
        "Approvazione massiva",
        `${failCount} elementi non approvati, riprova singolarmente.`,
      );
    onRefresh();
  };

  const handleRejectAll = async () => {
    setBulkLoading(true);
    const results = await Promise.allSettled(filteredItems.map(p => rejectOne(p, true)));
    const okCount = results.filter(r => r.status === "fulfilled" && r.value).length;
    const failCount = results.length - okCount;
    setBulkLoading(false);
    if (okCount) showNotification("success", "Rifiuto massivo", `${okCount} ${label} rifiutate.`);
    if (failCount)
      showNotification(
        "error",
        "Rifiuto massivo",
        `${failCount} elementi non rifiutati, riprova singolarmente.`,
      );
    onRefresh();
  };

    return (
    <Modal
      title={`Gestione ${label} da approvare`}
      visible={visible}
      onCancel={onClose}
      width={760}
      footer={
        <Row justify="space-between">
          <Col>
            <Popconfirm
              title={`Rifiutare tutte le ${filteredItems.length} anomalie in elenco?`}
              onConfirm={handleRejectAll}
              okText="Rifiuta tutti"
              cancelText="Annulla"
              disabled={!filteredItems.length}
            >
              <UIButton
                disabled={!filteredItems.length || bulkLoading}
                loading={bulkLoading}
                style={REJECT_BTN_STYLE}
              >
                <CloseOutlined /> Rifiuta tutti
              </UIButton>
            </Popconfirm>
          </Col>
          <Col>
            <Popconfirm
              title={`Approvare tutte le ${filteredItems.length} anomalie in elenco?`}
              onConfirm={handleApproveAll}
              okText="Approva tutti"
              cancelText="Annulla"
              disabled={!filteredItems.length}
            >
              <UIButton
                disabled={!filteredItems.length || bulkLoading}
                loading={bulkLoading}
                style={APPROVE_BTN_STYLE}
              >
                <CheckOutlined /> Approva tutti
              </UIButton>
            </Popconfirm>
          </Col>
        </Row>
      }
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <UISelect
            mode="multiple"
            allowClear
            showSearch
            style={{ width: "100%" }}
            placeholder={
              <span>
                <SearchOutlined style={{ marginRight: 5 }} /> Filtra per gruppo
              </span>
            }
            value={searchGroups}
            // @ts-ignore
            onChange={(value: string[]) => setSearchGroups(value)}
            maxTagCount="responsive"
            filterOption={(input, option) =>
              ((option!.children as unknown) as string).toLowerCase().includes(input.toLowerCase())
            }
          >
            {availableGroups.map(groupName => (
              <Option key={groupName} value={groupName}>
                {groupName}
              </Option>
            ))}
          </UISelect>
        </Col>
        <Col span={12}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Cerca per dipendente"
            value={searchEmployee}
            onChange={e => setSearchEmployee(e.target.value)}
            style={{ borderRadius: 7 }}
          />
        </Col>
      </Row>

      <ScrollArea>
        {!groupedItems.length ? (
          <UIText>
            {items.length
              ? "Nessun risultato per i filtri di ricerca."
              : "Nessuna anomalia da gestire per questo giorno."}
          </UIText>
        ) : (
          <StyledCollapse
            key={groupedItems.map(g => g.groupName).join("|")}
            defaultActiveKey={groupedItems.map(g => g.groupName)}
          >
            {groupedItems.map(group => (
              <Panel
                header={<UIText style={{ fontWeight: 600, fontSize: 14 }}>{group.groupName}</UIText>}
                key={group.groupName}
              >
                <DepartmentCollapse
                  ghost
                  key={`${group.groupName}|${group.departments.map(d => d.departmentName).join(",")}`}
                  defaultActiveKey={group.departments.map(d => d.departmentName)}
                >
                  {group.departments.map(department => (
                    <Panel
                      header={
                        <UIText
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#8c8c8c",
                            letterSpacing: "0.6px",
                            textTransform: "uppercase",
                          }}
                        >
                          {department.departmentName}
                        </UIText>
                      }
                      key={department.departmentName}
                    >
                      {department.presences.map((presence, index) => {
                        const delta = presence.modified?.delta_centesimal_duration ?? "";
                        const deltaSeconds = parseInt(
                          presence.modified?.delta_second_duration ?? "0",
                        );
                        const isRowLoading = loadingIds.includes(presence.original.id);

                        const timeFrom = moment(
                          presence.modified?.presence_datetime_from ??
                            presence.original.datetime_from,
                          "DD/MM/YYYY HH:mm:ss",
                        ).format("HH:mm");
                        const timeTo = presence.original.datetime_to
                          ? moment(
                              presence.modified?.presence_datetime_to ??
                                presence.original.datetime_to,
                              "DD/MM/YYYY HH:mm:ss",
                            ).format("HH:mm")
                          : "In corso";

                        const hasShiftPlanning = !!presence.modified?.shift_planning_id;
                        const shiftFrom = presence.modified?.shift_planning_datetime_from
                          ? moment(presence.modified.shift_planning_datetime_from).format("HH:mm")
                          : null;
                        const shiftTo = presence.modified?.shift_planning_datetime_to
                          ? moment(presence.modified.shift_planning_datetime_to).format("HH:mm")
                          : null;

                        return (
                          <PresenceRow key={presence.original.id}>
                            <Row align="middle" justify="space-between" gutter={[16, 8]}>
                              <Col span={17} style={{ display: "flex", alignItems: "flex-start" }}>
                                <Avatar
                                  src={presence.original.user.avatar}
                                  style={{ marginRight: 12, width: 34, height: 34, flexShrink: 0 }}
                                />
                                <div>
                                  <UIText style={{ fontWeight: 600, fontSize: 14 }}>
                                    {presence.original.user.surname} {presence.original.user.name}
                                  </UIText>

                                  <div style={{ marginTop: 6 }}>
                                    <UIText style={{ fontSize: 12, color: "#8c8c8c" }}>
                                      Turno assegnato:{" "}
                                    </UIText>
                                    <UIText style={{ fontSize: 12, fontWeight: 500 }}>
                                      {hasShiftPlanning && shiftFrom && shiftTo
                                        ? `${shiftFrom} - ${shiftTo} (${presence.modified?.shift_planning_centesimal_duration})`
                                        : "Nessun turno assegnato"}
                                    </UIText>
                                  </div>

                                  <div style={{ marginTop: 2 }}>
                                    <UIText style={{ fontSize: 12, color: "#8c8c8c" }}>
                                      Timbratura:{" "}
                                    </UIText>
                                    <UIText style={{ fontSize: 12, fontWeight: 500 }}>
                                      {timeFrom} - {timeTo}
                                    </UIText>
                                  </div>

                                  <div style={{ marginTop: 2 }}>
                                    <UIText style={{ fontSize: 12, color: "#8c8c8c" }}>
                                      Differenza:{" "}
                                    </UIText>
                                    <UIText
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: deltaSeconds < 0 ? "#c0392b" : "#5a8f2e",
                                      }}
                                    >
                                      {delta}
                                    </UIText>
                                  </div>
                                </div>
                              </Col>
                              <Col span={7} style={{ textAlign: "right" }}>
                                <UIButton
                                  block
                                  disabled={isRowLoading || bulkLoading}
                                  loading={isRowLoading}
                                  onClick={() => handleApproveOne(presence)}
                                  style={{ marginBottom: 8, ...APPROVE_BTN_STYLE }}
                                >
                                  <CheckOutlined /> Approva
                                </UIButton>
                                <UIButton
                                  block
                                  disabled={isRowLoading || bulkLoading}
                                  onClick={() => handleRejectOne(presence)}
                                  style={REJECT_BTN_STYLE}
                                >
                                  <CloseOutlined /> Rifiuta
                                </UIButton>
                              </Col>
                            </Row>
                            {index < department.presences.length - 1 && <PresenceDivider />}
                          </PresenceRow>
                        );
                      })}
                    </Panel>
                  ))}
                </DepartmentCollapse>
              </Panel>
            ))}
          </StyledCollapse>
        )}
      </ScrollArea>
    </Modal>
  );
};

const ScrollArea = styled.div`
  max-height: 55vh;
  overflow-y: auto;
  padding-right: 6px;

  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.25);
  }
`;

const PresenceRow = styled.div`
  padding: 12px 4px;
  border-radius: 6px;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #fafafa;
  }
`;

const PresenceDivider = styled.div`
  height: 1px;
  margin: 4px 0;
  background-color: #ececec;
`;

// Livello Gruppo: card con bordo, radius, leggera ombra — coerente col tema del calendario
const StyledCollapse = styled(Collapse)`
  background: transparent;

  & > .ant-collapse-item {
    border: 1px solid #bcbcbc;
    border-radius: 7px !important;
    margin-bottom: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }
  & > .ant-collapse-item:last-of-type {
    margin-bottom: 0;
  }
  & > .ant-collapse-item > .ant-collapse-header {
    background: rgb(${hexToRgb("" + BaseColor) + ", 0.25"});
    padding: 14px 20px !important;
    font-size: 15px;
  }
  & > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
    padding: 18px 20px !important;
    background: #fff;
  }
`;

// Livello Reparto: nessun riquadro, solo etichetta + separatore sottile
const DepartmentCollapse = styled(Collapse)`
  background: transparent;

  & > .ant-collapse-item {
    border: none;
    border-bottom: 1px solid #ececec;
  }
  & > .ant-collapse-item:last-of-type {
    border-bottom: none;
  }
  & > .ant-collapse-item > .ant-collapse-header {
    padding: 10px 0 !important;
  }
  & > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
    padding: 4px 0 12px !important;
    background: transparent;
  }
`;